import { ExpandMore, type SvgIconComponent } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccordionIds } from '~/hooks/unique-id';
import { FilterCheckbox, FilterRadio } from './styles';

// A filter row is a label around its checkbox or radio, so the control is the row's only
// interactive element and the row's text is its name.
const FilterListItemLabel = styled('label')(({ theme }) => ({
  alignItems: 'center',
  borderRadius: 4,
  cursor: 'pointer',
  display: 'flex',
  padding: theme.spacing(0, 2),
  width: '100%',
  '&:hover': { backgroundColor: theme.palette.action.hover },
}));

export interface FilterItem<T> {
  label: string;
  selected: boolean;
  value: T;
  count?: number;
}

export interface FilterCollapsibleListProps<T> {
  Icon?: SvgIconComponent;
  expanded?: boolean;
  items: FilterItem<T>[];
  label?: string;
  loading?: boolean;
  onClick: (value: T, newState: FilterItem<T>[]) => void;
  onExpand?: () => void;
  /** Use radio buttons for single-select (threshold filters like storage, temp, uptime) */
  singleSelect?: boolean;
}

export function FilterCollapsibleList<T>({
  Icon,
  expanded = false,
  loading = false,
  items,
  label,
  onClick,
  onExpand,
  singleSelect = false,
}: FilterCollapsibleListProps<T>) {
  const { t } = useTranslation();
  const accordionIds = useAccordionIds();
  const handleToggle = useCallback(
    (value: T) => {
      const isCurrentlySelected = items.find((x) => x.value === value)?.selected;

      const newState = items.map((x) => ({
        ...x,
        selected: singleSelect
          ? x.value === value && !isCurrentlySelected // Single select: toggle clicked, deselect others
          : x.value === value
          ? !x.selected // Multi select: toggle clicked item
          : x.selected,
      }));
      onClick(value, newState);
    },
    [items, onClick, singleSelect],
  );

  return (
    <Accordion
      disableGutters
      onChange={onExpand}
      expanded={expanded}
      sx={{
        '&::before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        {...accordionIds}
        expandIcon={<ExpandMore />}
        sx={{
          minHeight: '40px',
          '&.Mui-expanded': { minHeight: '40px' },
          '.MuiAccordionSummary-content': {
            fontSize: '.7rem',
            fontWeight: 600,
            margin: 0,
            textTransform: 'uppercase',
          },
        }}
      >
        {Icon && <Icon color="primary" sx={{ mr: 1 }} />}
        {label}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          '& > *': { width: '100%' },
          alignItems: 'flex-start',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          padding: (theme) => theme.spacing(1, 0, 0, 0),
        }}
      >
        <List dense sx={{ width: '100%' }}>
          {items.map(({ label, selected, value, count }) => (
            <ListItem key={label} disableGutters disablePadding>
              <FilterListItemLabel>
                <ListItemIcon>
                  {singleSelect ? (
                    <FilterRadio
                      edge="start"
                      checked={selected}
                      // A second click clears the selected radio, which change does not report.
                      inputProps={{ onClick: () => handleToggle(value) }}
                      size="small"
                    />
                  ) : (
                    <FilterCheckbox
                      edge="start"
                      checked={selected}
                      onChange={() => handleToggle(value)}
                      size="small"
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={label}
                  primaryTypographyProps={{ sx: { fontSize: '.875rem' } }}
                  sx={{ wordWrap: 'break-word' }}
                />
                {count !== undefined && (
                  <Chip
                    label={
                      loading ? (
                        <CircularProgress
                          aria-label={t('common.status.loading')}
                          size={12}
                          color="inherit"
                        />
                      ) : (
                        count
                      )
                    }
                    color={selected ? 'primary' : 'default'}
                  />
                )}
              </FilterListItemLabel>
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
}
